import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  CircleRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  tableContainer: {
    width: '100%',
    marginTop: tokens.spacingVerticalL,
  },
  trackerCell: {
    minWidth: '600px',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalM}`,
  },
  statusTracker: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: `${tokens.spacingVerticalM} 0`,
    position: 'relative',
  },
  connectorLine: {
    position: 'absolute',
    top: '14px',
    left: 'calc(100% / 8)',
    right: 'calc(100% / 8)',
    height: '3px',
    display: 'flex',
  },
  connectorSegment: {
    flex: 1,
    height: '100%',
  },
  connectorCompleted: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
  },
  connectorPending: {
    backgroundColor: tokens.colorNeutralStroke2,
  },
  stage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    position: 'relative',
    zIndex: 1,
  },
  stageIcon: {
    fontSize: '28px',
    width: '28px',
    height: '28px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stageIconCompleted: {
    color: tokens.colorPaletteGreenForeground1,
  },
  stageIconCurrent: {
    color: tokens.colorBrandForeground1,
  },
  stageIconPending: {
    color: tokens.colorNeutralForeground4,
  },
  stageLabel: {
    fontSize: tokens.fontSizeBase200,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    marginTop: tokens.spacingVerticalXXS,
  },
  stageLabelCompleted: {
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  stageLabelCurrent: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  stageLabelPending: {
    color: tokens.colorNeutralForeground4,
  },
  idCell: {
    fontFamily: tokens.fontFamilyMonospace,
    fontWeight: tokens.fontWeightSemibold,
  },
});

export type UploadStatus = 'Received' | 'Details Retrieved' | 'Preprocessing Complete' | 'Ready';

export interface UploadRecord {
  id: string;
  authorizationId: string;
  status: UploadStatus;
  uploadedAt: Date;
  errorMessage?: string;
}

interface StatusTrackerTableProps {
  uploads: UploadRecord[];
}

const STAGES: UploadStatus[] = [
  'Received',
  'Details Retrieved',
  'Preprocessing Complete',
  'Ready',
];

export const StatusTrackerTable: React.FC<StatusTrackerTableProps> = ({ uploads }) => {
  const classes = useStyles();

  const getStageIndex = (status: UploadStatus): number => {
    return STAGES.indexOf(status);
  };

  const renderStatusTracker = (currentStatus: UploadStatus) => {
    const currentIndex = getStageIndex(currentStatus);

    return (
      <div className={classes.statusTracker}>
        {/* Connector line segments */}
        <div className={classes.connectorLine}>
          {STAGES.slice(0, -1).map((_, index) => {
            const isCompleted = index < currentIndex;
            return (
              <div
                key={`connector-${index}`}
                className={`${classes.connectorSegment} ${
                  isCompleted ? classes.connectorCompleted : classes.connectorPending
                }`}
              />
            );
          })}
        </div>

        {/* Stages */}
        {STAGES.map((stage, index) => {
          // Ready is the final stage - when reached, it should show as completed
          const isCompleted = index < currentIndex || (stage === 'Ready' && currentStatus === 'Ready');
          const isCurrent = index === currentIndex && stage !== 'Ready';

          return (
            <div key={stage} className={classes.stage}>
              {/* Stage icon with background */}
              <div className={classes.stageIcon}>
                {isCompleted ? (
                  <CheckmarkCircleRegular
                    className={classes.stageIconCompleted}
                  />
                ) : isCurrent ? (
                  <CircleRegular
                    className={classes.stageIconCurrent}
                    filled={true}
                  />
                ) : (
                  <CircleRegular
                    className={classes.stageIconPending}
                  />
                )}
              </div>

              {/* Stage label */}
              <Text
                size={200}
                className={`${classes.stageLabel} ${
                  isCompleted
                    ? classes.stageLabelCompleted
                    : isCurrent
                    ? classes.stageLabelCurrent
                    : classes.stageLabelPending
                }`}
              >
                {stage}
              </Text>
            </div>
          );
        })}
      </div>
    );
  };

  if (uploads.length === 0) {
    return null;
  }

  return (
    <div className={classes.tableContainer}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Authorization ID</TableHeaderCell>
            <TableHeaderCell className={classes.trackerCell}>Status</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {uploads.map((upload) => (
            <TableRow key={upload.id}>
              <TableCell className={classes.idCell}>{upload.authorizationId}</TableCell>
              <TableCell className={classes.trackerCell}>
                {renderStatusTracker(upload.status)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
